import { ArrowEdgeFlow, Box, theme } from '@/components';
import {
  OFFSET_GRAPH_HEADING_HEIGHT,
  PAPER_FILE_SIZE,
} from '@/features/mold/utils';
import {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  type Edge,
  type Node,
  type OnNodesChange,
  useNodesState,
} from 'reactflow';
import { CustomNode } from './CustomNode';
import { activityTreeZIndexes } from './constants';

type Props = {
  initialNodes: Node[];
  initialEdges: Edge[];
  setTreeModifyCount?: Dispatch<SetStateAction<number>>;
  panelNode?: (elements: { nodes: Node[]; edges: Edge[] }) => ReactNode;
  hideBorder?: boolean;
};
/**
 * It is recommended on the library side to define this outside the component.
 * @see https://reactflow.dev/learn/troubleshooting#002
 */
const nodeTypes = { customNode: CustomNode };

export const ActivityTree: FC<Props> = ({
  initialNodes,
  initialEdges,
  panelNode,
  setTreeModifyCount,
  hideBorder = false,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const boxStyle = {
    width: 320,
    background: 'transparent',
    border: `4px solid ${token.colorBorder}`,
    fontSize: 'large',
  } as const satisfies Node['style'];

  const borderBoxNodes: Node[] = [
    {
      id: 'boxOfactivity',
      data: { label: t('activity') },
      draggable: false,
      position: { x: 0, y: 0 },
      style: boxStyle,
      zIndex: activityTreeZIndexes.guideBox,
    },
    {
      id: 'boxOfoutputIndicator',
      data: { label: t('output') },
      draggable: false,
      position: { x: 350, y: 0 },
      style: boxStyle,
      zIndex: activityTreeZIndexes.guideBox,
    },
    {
      id: 'boxOfshortOutcomeIndicator',
      data: { label: t('short-term') + t('outcome') },
      draggable: false,
      position: { x: 700, y: 0 },
      style: boxStyle,
      zIndex: activityTreeZIndexes.guideBox,
    },
    {
      id: 'boxOfmediumOutcomeIndicator',
      data: { label: t('medium-term') + t('outcome') },
      draggable: false,
      position: { x: 1050, y: 0 },
      style: boxStyle,
      zIndex: activityTreeZIndexes.guideBox,
    },
    {
      id: 'boxOflongOutcomeIndicator',
      data: { label: t('long-term') + t('outcome') },
      draggable: false,
      position: { x: 1400, y: 0 },
      style: boxStyle,
      zIndex: activityTreeZIndexes.guideBox,
    },
  ];

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  const displayNodes = useMemo(
    () => [...borderBoxNodes, ...initialNodes],
    [initialNodes],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>(displayNodes);
  const [edges, setEdges] = useState<Edge[]>(initialEdges);

  const updateBoxHeights = useCallback(() => {
    let maxYPosition = Number.NEGATIVE_INFINITY;

    for (const node of nodes) {
      if (
        !node.id.startsWith('boxOf') &&
        node.height &&
        node.position.y + Number(node.height) > maxYPosition
      ) {
        maxYPosition = node.position.y + Number(node.height);
      }
    }

    if (maxYPosition < 300) {
      maxYPosition = 300;
    }
    const newHeight = maxYPosition + 50;
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id.startsWith('boxOf')) {
          return {
            ...node,
            style: { ...node.style, height: newHeight },
          };
        }
        return {
          ...node,
          // I don't know why but this line helps to ensure the node height isn't undefined when adding new nodes.
          style: { ...node.style, height: node.height ?? 'undefined' },
        };
      }),
    );
  }, [nodes, setNodes]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 既存のDependencyListを変更した際の影響が読めないためignoreしておく。把握している人がいればignoreがなくなるよう適切に変更すること。
  useEffect(() => {
    setTreeModifyCount?.(0);
    setNodes(displayNodes);
    setEdges(initialEdges);
  }, [
    initialNodes,
    initialEdges,
    setNodes,
    setEdges,
    displayNodes,
    setTreeModifyCount,
  ]);

  const handleNodeChange = useCallback<OnNodesChange>(
    (changes) => {
      setTreeModifyCount?.((prev) => prev + 1);
      onNodesChange(changes);
      updateBoxHeights();
    },
    [setTreeModifyCount, onNodesChange, updateBoxHeights],
  );

  const boxNodeIds = borderBoxNodes.map((boxNode) => boxNode.id);
  const activityNodes = nodes.filter((node) => !boxNodeIds.includes(node.id));

  return (
    <Box
      // To fit in A4 with RsProjectLedgerPage.
      height={`calc(${PAPER_FILE_SIZE.A4.width} - ${OFFSET_GRAPH_HEADING_HEIGHT})`}
      width={PAPER_FILE_SIZE.A4.height}
      border={hideBorder ? undefined : `1px solid ${token.colorBorder}`}
      borderRadius={hideBorder ? undefined : 8}
    >
      <ArrowEdgeFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={handleNodeChange}
      >
        {panelNode?.({ nodes: activityNodes, edges })}
      </ArrowEdgeFlow>
    </Box>
  );
};
